import type { ChainKey } from '@infinex/chain-config';
import type { BlockaidValidationResult } from '@infinex/connect-service-contract/client';
import { cn } from '@infinex/ui-styles';
import { Text } from '@infinex/ui/components';

import { AssetApprovalItem } from './AssetApprovalItem';
import { AssetDiffItem } from './AssetDiffItem';
import {
  normalizeChainValidationResponse,
  type NormalizedAssetApproval,
} from './normalizeChainResponse';

import { MaliciousTransactionCard } from '../MaliciousTransactionCard';
import { SignableInfoItem } from '../SignableInfoItem';

export type ApprovalEditState = {
  /** Map of "token:spender" to modified formatted amount */
  modifiedAmounts: Map<string, string>;
};

type ValidationResultCardProps = {
  txnValidationResult?: BlockaidValidationResult;
  chainKey: ChainKey;
  rawMessage?: string;
  title?: string;
  className?: string;
  onMaliciousClick?: () => void;
  /** Whether approvals can be edited (EVM userOperations only) */
  isApprovalEditable?: boolean;
  /** Current modification state for approvals */
  approvalEditState?: ApprovalEditState;
  /** Called when user wants to edit an approval */
  onApprovalEditPress?: (approval: NormalizedAssetApproval) => void;
};

export function ValidationResultCard({
  txnValidationResult,
  chainKey,
  rawMessage,
  title,
  className,
  onMaliciousClick,
  isApprovalEditable = false,
  approvalEditState,
  onApprovalEditPress,
}: ValidationResultCardProps) {
  const validationResult = txnValidationResult
    ? normalizeChainValidationResponse({ chainKey, txnValidationResult })
    : null;

  // Check if transaction is malicious
  // For EVM - check validation.result_type
  // For Solana - check result.validation.result_type
  const isMalicious =
    txnValidationResult &&
    (('validation' in txnValidationResult &&
      txnValidationResult.validation?.result_type === 'Malicious') || // EVM
      ('result' in txnValidationResult &&
        txnValidationResult.result?.validation?.result_type === 'Malicious')); // SOLANA

  const diffItems = validationResult?.assetDiffs || [];
  const approvalItems = validationResult?.assetApprovals || [];

  const hasContent =
    diffItems.length > 0 ||
    approvalItems.length > 0 ||
    rawMessage ||
    isMalicious;

  const hasAssetChanges = diffItems.length > 0 || approvalItems.length > 0;

  return (
    <div className={cn('bg-surface rounded-lg p-4', className)}>
      {!hasContent ? (
        <div className="mt-1">
          <Text className="text-tertiary text-center text-xs font-semibold">
            Unable to simulate transaction
          </Text>
        </div>
      ) : (
        <>
          {hasAssetChanges ? (
            <Text className="text-secondary mb-3 text-sm font-semibold">
              {title || 'Summary'}
            </Text>
          ) : null}

          {isMalicious ? (
            <MaliciousTransactionCard
              onClick={onMaliciousClick}
              className="my-2"
            />
          ) : null}

          {diffItems.map((item, index) => (
            <AssetDiffItem
              key={`diff-${item.asset.type === 'native' ? 'native' : item.asset.address}-${index}`}
              asset={item.asset}
              amount={item.amount}
              usdValue={item.usdValue}
              isReceiving={item.isReceiving}
            />
          ))}

          {approvalItems.map((item, index) => {
            const approvalKey =
              item.asset.type !== 'native'
                ? `${item.asset.address?.toLowerCase()}:${item.spenderAddress.toLowerCase()}`
                : `native:${item.spenderAddress.toLowerCase()}`;
            const modifiedAmount =
              approvalEditState?.modifiedAmounts.get(approvalKey);
            const isModified = modifiedAmount !== undefined;

            return (
              <AssetApprovalItem
                key={`approval-${item.asset.type === 'native' ? 'native' : item.asset.address}-${item.spenderAddress}-${index}`}
                asset={item.asset}
                amount={item.amount}
                usdValue={item.usdValue}
                spenderAddress={item.spenderAddress}
                isEditable={isApprovalEditable}
                isModified={isModified}
                modifiedAmount={modifiedAmount}
                onEditPress={
                  onApprovalEditPress
                    ? () => onApprovalEditPress(item)
                    : undefined
                }
              />
            );
          })}

          {rawMessage ? (
            <SignableInfoItem
              rawData={rawMessage}
              className="mt-3"
              title={hasAssetChanges ? 'Message' : undefined}
            />
          ) : null}
        </>
      )}
    </div>
  );
}
